import React, { StrictMode, useEffect } from "react";
import { Container, createRoot } from "react-dom/client";
import { Slide, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.min.css";
import "./utils/i18next";
import { OMSpinner } from "./components/atom/Spinner";
import { useInit } from "./utils/hooks/useInit";
import { TRANSLATION_CONFIG } from "./utils/consts/translation";
import { ScrollToTop } from "./utils/scroll";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";
import { REACT_ROUTER_FUTURE } from "./utils/consts/routerFuture";
import { ROUTES } from "./utils/consts/routes";
import { DefaultLayout } from "./components/molecules/layout";
import { DataProvider } from "./utils/contexts/UserProvider";
import { Login } from "./components/organisms/auth/Login";
import { Dashboard } from "./components/organisms/dashboard";
import { ProtectedRoute } from "./utils/route";
import { AuthProvider } from "./utils/contexts/AuthProvider";
import { Journey } from "./components/organisms/journey";
import "./css/style.css";
import { Exams } from "./components/organisms/exams";
import { ForgotPassword } from "./components/organisms/auth/ForgotPassword";
import { ResetPassword } from "./components/organisms/auth/ResetPassword";
import { ImpersonateUser } from "./components/organisms/auth/ImpersonateUser";
import { Documents } from "./components/organisms/documents";
import { Practice } from "./components/organisms/practice";
import * as Sentry from "@sentry/react";
import { isDevlopment } from "./utils/env";
import { Settings } from "luxon";
import { LegalDocuments } from "./components/organisms/legal-documents/LegalDocuments";
import { ProfessionalTraining } from "./components/organisms/professional-training";

Settings.defaultZone = "UTC";

const App = () => {
  const { loading } = useInit(TRANSLATION_CONFIG.appNamespace);

  if (loading) {
    return <OMSpinner />;
  }

  return (
    <AuthProvider>
      <DataProvider>
        <Router future={REACT_ROUTER_FUTURE}>
          <ScrollToTop />
          <Routes>
            <Route path={ROUTES.home} element={<Login />} />
            <Route path={ROUTES.forgotPassword} element={<ForgotPassword />} />
            <Route path={ROUTES.resetPassword} element={<ResetPassword />} />
            <Route path={ROUTES.impersonateUser} element={<ImpersonateUser />} />
            <Route
              element={
                <ProtectedRoute>
                  <DefaultLayout />
                </ProtectedRoute>
              }>
              <Route path={ROUTES.dashboard} element={<Dashboard />} />
              <Route path={ROUTES.exams} element={<Exams />} />
              <Route path={ROUTES.documents} element={<Documents />} />
              <Route path={ROUTES.practice} element={<Practice />} />
              <Route path={ROUTES.professionalTraining} element={<ProfessionalTraining />} />
              <Route path={ROUTES.legalDocuments} element={<LegalDocuments />} />
            </Route>
            <Route
              element={
                <ProtectedRoute>
                  <DefaultLayout hideNavbar />
                </ProtectedRoute>
              }>
              <Route path={ROUTES.exam} element={<Journey />} />
            </Route>
          </Routes>
        </Router>
      </DataProvider>
    </AuthProvider>
  );
};

Sentry.init({
  dsn: "https://97e7977c658d09b73d68cd18abd79dad@sentry.xcentric.it/8",
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: !isDevlopment() ? 0.2 : 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

const root = createRoot(document?.getElementById("app") as Container);
root.render(
  <StrictMode>
    <ToastContainer
      progressStyle={{
        background: "#574CF9",
      }}
      transition={Slide}
      position="top-center"
      autoClose={5000}
      hideProgressBar={false}
      newestOnTop={false}
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
    />
    <App />
  </StrictMode>,
);
